import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, useMediaQuery, Chip } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useFetch } from '../../hooks/useFetch';

const ROTA_AO_VIVO = '/lista/ao-vivo';

// Bolinha vermelha pulsando: so aparece quando existe transmissao rolando
const PontoAoVivo = () => (
    <Box
        component="span"
        sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#d32f2f',
            flexShrink: 0,
            animation: 'pulsarAoVivo 2.2s ease-in-out infinite',
            '@keyframes pulsarAoVivo': {
                '0%, 100%': { opacity: 1, boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.5)' },
                '50%': { opacity: 0.35, boxShadow: '0 0 0 4px rgba(211, 47, 47, 0)' },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
    />
);

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery('(max-width:900px)');
    const { user } = useAuth();

    // Esconder sidebar em telas de transmissao e chamada
    const hiddenRoutes = ['/lista/ao-vivo/', '/chamada'];
    const oculta = hiddenRoutes.some(route => location.pathname.includes(route));

    // Transmissoes no ar agora. So vale a pena consultar no celular e quando a
    // barra aparece; o SWR reaproveita a mesma consulta da tela de ao vivo
    const { data: aoVivo } = useFetch('/search/live', undefined, !isMobile || oculta, {
        refreshInterval: 60000,
    });
    // Mesma contagem da tela de ao vivo: cada encontro pode ter varias sessoes
    const temTransmissao = (aoVivo || []).some(
        encontro => (encontro.meeting_schedule || []).length > 0
    );

    if (oculta) return null;

    const items = [
        { label: 'Acontecendo agora', path: ROTA_AO_VIVO },
        { label: 'Encontros comprados/agendados', path: '/encontros' },
        ...(Boolean(user?.profile)
            ? [{ label: 'Encontros criados', path: '/meus-encontros' }]
            : []
        ),
        { label: 'Calendário', path: '/calendario' },
    ];

    if (isMobile) {
        // No celular so fica o atalho de "Acontecendo agora": o resto virou item
        // do menu lateral do hamburguer, que ja tem o espaco para eles
        const itensMobile = items.filter(item => item.path === ROTA_AO_VIVO);

        return (
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                width: '100%',
                px: 1.5,
                py: 1,
            }}>
                {itensMobile.map((item) => {
                    const isActive = location.pathname === item.path;
                    // O ponto entra dentro do label (e nao no prop icon) porque o
                    // Chip clona o icone injetando className, que a bolinha ignora
                    const sinalizar = temTransmissao && item.path === ROTA_AO_VIVO;
                    return (
                        <Chip
                            key={item.path}
                            label={
                                sinalizar ? (
                                    <Box
                                        component="span"
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                                    >
                                        <PontoAoVivo />
                                        {item.label}
                                    </Box>
                                ) : (
                                    item.label
                                )
                            }
                            title={sinalizar ? 'Tem encontro sendo transmitido agora' : undefined}
                            onClick={() => navigate(item.path)}
                            variant={isActive ? 'filled' : 'outlined'}
                            size="small"
                            sx={{
                                fontWeight: isActive ? 600 : 400,
                                bgcolor: isActive ? '#e6f3f1' : 'transparent',
                                borderColor: '#ccc',
                                color: isActive ? '#006a61' : 'text.primary',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: '#f0f7f6' },
                            }}
                        />
                    );
                })}
            </Box>
        );
    }

    // Desktop: posição fixa, alinhada com a borda direita do conteúdo da página de encontros
    // O conteúdo usa width:90% centralizado, então a sidebar fica a ~5% da borda direita
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 100,
                right: 'calc(5% + 16px)',
                width: 150,
                zIndex: 10,
            }}
        >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Navegar
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <Box
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                py: 0.5,
                                borderRadius: '6px',
                                '&:hover .sidebar-label': { color: '#006a61' },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    bgcolor: isActive ? '#006a61' : '#999',
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                className="sidebar-label"
                                variant="body2"
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: isActive ? 600 : 400,
                                    color: isActive ? '#006a61' : 'text.secondary',
                                    transition: 'color 0.2s',
                                }}
                            >
                                {item.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Sidebar;
